import { UTCDate } from '@date-fns/utc';
import { subWeeks, subYears } from 'date-fns';
import type { Client } from 'urql';

import { MonthWeekIndexQuery, TrialType } from '@revelio/data-access';

/**
 * The account-specific latest data date a relative-window PRESET must anchor
 * on, read through the urql CLIENT rather than off a render.
 *
 * dash-meta resolves a preset against the GLOBAL data max — its index query
 * runs with admin auth and has no notion of an account's trial lag (see
 * `DateRangeResolver`, which carries the preset token forward precisely so the
 * client can re-resolve it). A lagged account's window therefore has to be
 * re-anchored here, and until now that depended on
 * `useAvailableDateRangeWithTrialLag` having already resolved AT RENDER TIME.
 * Two ways that failed on a cold mount, both silent (Greptile P1 on #6499):
 *
 *  - the index query was still `fetching`, so the hook returned null and the
 *    server's globally anchored window applied unchanged — for a lagged
 *    account that window can sit entirely past the end of its data, and the
 *    chart comes back empty;
 *  - worse, `useUserTrialDetails` publishes `trialType` from an EFFECT, and
 *    the assistant's headless digest host applies filters in a LAYOUT effect,
 *    which runs first. So even with the index warm, `trialType` read
 *    `undefined`, `isLagged` was false, and the anchor was the GLOBAL max
 *    presented as if it were the account's. That is the same ordering trap
 *    `isSelfSignupUser` already had to be moved off in this directory.
 *
 * Reading through the client fixes both: a warm cache resolves without a
 * network round trip, a cold one awaits the fetch instead of guessing, and the
 * lag comes from `getUserTrialType` — a pure function over the logged-in user
 * — rather than from effect-published state.
 */
export const resolvePresetDateAnchor = async ({
  client,
  granularity,
  trialType,
}: {
  client: Client;
  granularity: 'month' | 'week';
  /**
   * `getUserTrialType`'s return, passed straight through — `false` for a live
   * account. Deliberately NOT widened to `undefined`: that helper is total over
   * the user record and defaults an unloaded one to `ONE_YEAR_DATA_LAG`, which
   * is the conservative direction (a lagged window is a subset of the
   * account's data; a global one can sit entirely past the end of it). Leaving
   * `undefined` out means this function has no third case to invent a policy
   * for.
   */
  trialType: TrialType | false;
}): Promise<UTCDate | null> => {
  const result = await client
    .query(MonthWeekIndexQuery, {}, { requestPolicy: 'cache-first' })
    .toPromise();

  const raw =
    granularity === 'month'
      ? result.data?.index?.endMonth
      : result.data?.index?.maxWeek;
  if (!raw) return null;

  const globalMax = new UTCDate(raw);
  if (trialType !== TrialType.ONE_YEAR_DATA_LAG) return globalMax;

  // Same arithmetic as `useAvailableDateRangeWithTrialLag`, which is what the
  // manual date filter shows this account — the AI window must not be able to
  // reach past what the picker would let the user select.
  return (
    granularity === 'month' ? subYears(globalMax, 1) : subWeeks(globalMax, 53)
  ) as UTCDate;
};
